import React, { useState, useEffect } from 'react';
import styles from './CountdownTimer.module.css';

interface CountdownTimerProps {
  endDate: Date | string;
  title?: string;
  hideWhenPast?: boolean;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({ 
  endDate, 
  title = "We'll be back online in",
  hideWhenPast = false
}) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null);

  useEffect(() => {
    const calculateTimeLeft = (): TimeLeft | null => {
      const end = typeof endDate === 'string' ? new Date(endDate) : endDate;
      const now = new Date();
      const difference = end.getTime() - now.getTime();

      if (difference <= 0) {
        return { days: 0, hours: 0, minutes: 0 };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.ceil((difference % (1000 * 60 * 60)) / (1000 * 60)),
      };
    };

    // Calculate immediately
    setTimeLeft(calculateTimeLeft());

    // Update every second for real-time countdown
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000); // Update every second

    return () => clearInterval(timer);
  }, [endDate]);

  if (timeLeft === null) {
    return null;
  }

  if (hideWhenPast && timeLeft.days === 0 && timeLeft.hours === 0 && timeLeft.minutes === 0) {
    return null;
  }

  const formatTime = (num: number): string => {
    return String(num).padStart(2, '0');
  };

  return (
    <div className={styles.countdownContainer}>
      <div className={styles.countdownBox}>
        <h3 className={styles.title}>{title}</h3>
        <div className={styles.countdownNumbers}>
          <div className={styles.timeGroup}>
            <div className={styles.timeValue}>{formatTime(timeLeft.days)}</div>
            <div className={styles.timeLabel}>Days</div>
          </div>
          <div className={styles.separator}>:</div>
          <div className={styles.timeGroup}>
            <div className={styles.timeValue}>{formatTime(timeLeft.hours)}</div>
            <div className={styles.timeLabel}>Hours</div>
          </div>
          <div className={styles.separator}>:</div>
          <div className={styles.timeGroup}>
            <div className={styles.timeValue}>{formatTime(timeLeft.minutes)}</div>
            <div className={styles.timeLabel}>Minutes</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CountdownTimer;

